import { createContext, FC, ReactElement, useCallback, useContext } from 'react'
import config from '../../config'
import { create as createHomologation } from '../../externals/api/homologations'
import { getSignedUploadUrl, uploadToS3 } from '../../externals/api/upload'
import { useAlertHandler } from '../alertHandler'

export type Homologation = {
  labName: string
  state: string
  files: File[]
}

interface IRequestNewLab {
  create: (params: Homologation) => Promise<void>;
}

const RequestNewLabContext = createContext<IRequestNewLab>({} as IRequestNewLab)

export const useRequestNewLab = (): IRequestNewLab => {
  const context = useContext(RequestNewLabContext)
  if (!context) {
    throw new Error('useRequestNewLab must be used within an RequestNewLabContext')
  }

  return context
}

export const RequestNewLabProvider: FC = ({ children }): ReactElement => {
  const { showError } = useAlertHandler()

  const create = useCallback(async (params: Homologation): Promise<void> => {
    try {
      const files = await Promise.all(params.files.map(async (file: File): Promise<string> => {
        const { headers, url } = await getSignedUploadUrl({
          filename: file.name,
          contentType: file.type,
          environment: config.ENVIRONMENT,
          folder: 'homologations',
          genDiffFilename: false
        })

        const newHeaders = {
          ...headers,
          'Content-Type': file.type
        }

        await uploadToS3(url, file, newHeaders)

        return url.split('?')[0] as string
      }))

      await createHomologation({
        ...params,
        files
      })
    } catch (err) {
      showError(err)

      throw err
    }
  }, [showError])

  return (
    <RequestNewLabContext.Provider
      value={{
        create
      }}
    >
      {children}
    </RequestNewLabContext.Provider>
  )
}
